/**
 * Location / Device schemas — practice sites, rooms, equipment.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type { LocationStatus as FhirLocationStatus, PracticeSiteRoomTypeCode } from '@unify/fhir-model/resources/location';
import type { DevicePortabilityCode } from '@unify/fhir-model/resources/device';

/** FHIR Location.status values — synced with fhir-model. */
export const locationStatusValues = [
  'active',
  'inactive',
] as const satisfies readonly FhirLocationStatus[];

/** Room type codes — synced with fhir-model. */
export const roomTypeValues = [
  'therapy-room',
  'phone-booth',
] as const satisfies readonly PracticeSiteRoomTypeCode[];

/** Device portability codes — synced with fhir-model. */
export const devicePortabilityValues = [
  'fixed',
  'portable',
] as const satisfies readonly DevicePortabilityCode[];

// ═══════════════════════════════════════════════════════════════════════
//  Practice Site
// ═══════════════════════════════════════════════════════════════════════

/**
 * Create / update a practice site (Location).
 */
export const practiceSiteSchema = z.object({
  name: z.string().trim().min(1, 'Location name is required'),
  addressLine1: z.string().trim().min(1, 'Street address is required'),
  addressLine2: z.string().trim().optional(),
  city: z.string().trim().min(1, 'City is required'),
  state: z.string().trim().min(1, 'State is required'),
  zipCode: z.string().trim().min(1, 'Zip code is required'),
  phone: z.string().trim().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  status: z.enum(locationStatusValues).default('active'),
});

export type PracticeSiteInput = z.infer<typeof practiceSiteSchema>;

// ═══════════════════════════════════════════════════════════════════════
//  Room
// ═══════════════════════════════════════════════════════════════════════

/**
 * Create / update a room within a practice site (Location with partOf).
 */
export const roomSchema = z.object({
  name: z.string().trim().min(1, 'Room name is required'),
  type: z.enum(roomTypeValues),
  capacity: z.number().int().min(1, 'Capacity must be at least 1'),
  adaAccessible: z.boolean(),
  status: z.enum(locationStatusValues),
});

export type RoomInput = z.infer<typeof roomSchema>;

// ═══════════════════════════════════════════════════════════════════════
//  Equipment (Device)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Create / update a practice equipment device.
 */
export const equipmentSchema = z
  .object({
    name: z.string().trim().min(1, 'Equipment name is required'),
    portability: z.enum(devicePortabilityValues),
    homeRoomId: z.string().optional(),
    status: z.enum(locationStatusValues),
  })
  .refine(
    (data) => data.portability !== 'fixed' || (data.homeRoomId && data.homeRoomId.length > 0),
    { message: 'A home room is required for fixed equipment', path: ['homeRoomId'] },
  );

export type EquipmentInput = z.infer<typeof equipmentSchema>;
